import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { mcpRiskLevelSchema, mcpServerRiskSchema } from './mcp';
import { aiInstructionUserBasicSchema } from './ai-instructions';
import { skillVersionSchema } from './policy-exceptions';
import { ColumnDataTypes, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
/**
 * Inventory → Skills (KIR-4699).
 *
 * Identity is `(name, source)`, not the content id AI Instructions groups on:
 * the same SKILL.md in a project and in `~/.claude/skills` is two skills,
 * marked independently — hence its own table.
 */
/** No Allow list exists, so a row is one or the other. */
const SKILL_LIST_STATES = ['allowed', 'marked_for_delete'];
/**
 * A dedicated query param rather than a column filter, like the Packages
 * page: the state is computed from the rule list, not stored on the row.
 */
const SKILL_LIST_STATE_FILTERS = [
    'all',
    'allowed',
    'marked_for_delete',
];
export const skillListStateFilterSchema = z.enum(SKILL_LIST_STATE_FILTERS);
const skillListStateSchema = z.enum(SKILL_LIST_STATES);
/**
 * One project a skill was seen in.
 *
 * An object rather than a bare name (KIR-4850 review): project names are
 * repo/folder basenames, so `github.com/acme/api` and `github.com/globex/api`
 * are both "api". Keying the cell count and the drilldown on the name made a
 * skill in two such repos render "1" while the Projects sort — which counts
 * distinct project ids — ordered it as 2.
 *
 * `slug` is the stable per-organization key (and the value the Projects
 * filter sends back); `origin` is the normalized git origin, used to tell
 * two same-named projects apart and null for a repo-less folder.
 */
export const skillProjectRefSchema = z.object({
    slug: z.string(),
    name: z.string(),
    origin: z.string().nullable(),
});
/**
 * Why a skill has no AgentMesh verdict (KIR-5099): `pending` (not looked up
 * yet), `agentmesh_not_configured` (this deployment has no AgentMesh URL, so
 * nothing is looked up), `not_in_agentmesh` (no scanned skill with these exact
 * files), `unhashable` (the agent could not hash the folder), `not_reported`
 * (no hash sent: older agent, or no SKILL.md).
 */
export const skillRiskUnavailableReasonSchema = z.enum([
    'pending',
    'agentmesh_not_configured',
    'not_in_agentmesh',
    'unhashable',
    'not_reported',
]);
export const skillDataTableItemSchema = z.object({
    /** Folded skill name — the first half of the row's identity. */
    name: z.string(),
    /**
     * The skill directory as the author cased it, for the Name column. `name`
     * is folded so the row, the rule, and enforcement agree on one key; showing
     * it verbatim would render `PDF-Processing` as `pdf-processing`. Falls back
     * to `name` when the path cannot confirm a casing. Display only — never
     * send this back as a rule.
     */
    displayName: z.string(),
    /** `project`, `user`, or `plugin:<plugin-name>` — the second half. */
    source: z.string(),
    /**
     * The third half: which project a `project`-source skill belongs to, as the
     * normalized git origin. Null for `user` / `plugin:` skills and for project
     * skills whose client reports no origin. Send it back verbatim in the rule
     * object — two repos' same-named skills are two rows, marked separately.
     */
    project: z.string().nullable(),
    /**
     * The fourth half (KIR-5174): which version of the skill this row is, as
     * the hash and the scheme it was computed under (`skill-md-v1`: the
     * SKILL.md hash). Two same-named skills whose SKILL.md differs are two
     * rows. A mark writes the hash under the scheme's rule field
     * (`SKILL_VERSION_RULE_FIELDS`) so it covers this version only. Null while
     * the version is not known yet (a supporting file seen before its
     * manifest).
     */
    version: skillVersionSchema.nullable(),
    /** Representative path, shown under the name. */
    filePath: z.string(),
    filePaths: z.array(z.string()),
    /** The manifest's content, for the expanded row's file preview. */
    contentId: z.string().uuid().nullable(),
    summary: z.string().nullable(),
    users: z.array(aiInstructionUserBasicSchema),
    projects: z.array(skillProjectRefSchema),
    /**
     * KIR-5099: the AgentMesh verdict for the skill's files, looked up by the
     * `skill-tree-v1` hash the agent computes over the skill's folder. The worst
     * verdict wins across the skill's copies. Null when there is no verdict,
     * with the reason in `riskUnavailableReason`; optional only for a backend
     * that predates it.
     */
    risk: mcpServerRiskSchema.nullable().optional(),
    riskUnavailableReason: skillRiskUnavailableReasonSchema.nullable().optional(),
    /**
     * @deprecated Always null. Sent for one release after KIR-5099 so a page
     * loaded before the deploy, which requires it, still parses the list.
     */
    riskLevel: z.null().optional(),
    listState: skillListStateSchema,
    /** KIR-4701: group-scoped lists only — whose choice `listState` is. */
    enforcementSource: z.enum(['group', 'organization']).optional(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
/** One AgentMesh skill whose scanned files equal a copy's files. */
export const skillRiskSourceSchema = z.object({
    name: z.string().nullable(),
    /** The repository folder AgentMesh scanned, as a GitHub URL. */
    gitRepo: z.string().nullable(),
    level: mcpRiskLevelSchema,
});
/**
 * One distinct set of files among a skill's copies (one `skill-tree-v1`
 * hash, or a copy that could not be hashed), with its own verdict.
 */
export const skillRiskCopySchema = z.object({
    skillTreeSha256: z.string().nullable(),
    /** How many of the skill's users have this copy. */
    userCount: z.number().int().min(0),
    risk: mcpServerRiskSchema.nullable(),
    riskUnavailableReason: skillRiskUnavailableReasonSchema.nullable(),
    /** Top risk factors of the worst matching AgentMesh scan, worst first. */
    riskFactors: z.array(z.string()),
    /** How many AgentMesh skills have exactly these files. */
    matchCount: z.number().int().min(0).nullable(),
    /** The matching AgentMesh skills, worst first (at most five). */
    sources: z.array(skillRiskSourceSchema),
    /** "View in AgentMesh" for the worst match; null without one. */
    agentmeshUrl: z.string().url().nullable(),
    /** `.git` entries in the folder, which the hash does not cover. */
    excludedPaths: z.array(z.string()),
    /**
     * The verdict is for the folder without `localOnlyPaths` (tool caches, OS
     * and editor files), which the repository's
     * copy that AgentMesh scanned does not have; the verdict does not cover
     * them.
     */
    leavesOutLocalFiles: z.boolean(),
    localOnlyPaths: z.array(z.string()),
    /** Why the agent could not hash the folder, as it reported it. */
    hashError: z.string().nullable(),
});
/** `GET /organizations/:id/skills/risk`: the expanded row's risk panel. */
export const skillRiskQuerySchema = z.object({
    name: z.string().min(1).max(500),
    source: z.string().min(1).max(500),
    project: z.string().max(2000).optional(),
    /** The row's `version.hash`; omitted for a row whose version is unknown. */
    versionHash: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .optional(),
    /** The group list the row is in (KIR-4701): its marks decide the copies. */
    securityGroupId: z.string().uuid().optional(),
});
export const skillRiskDetailSchema = z.object({
    /** Same value as the row's `risk`. */
    risk: mcpServerRiskSchema.nullable(),
    riskUnavailableReason: skillRiskUnavailableReasonSchema.nullable(),
    /** Worst first. */
    copies: z.array(skillRiskCopySchema),
});
const SKILL_COLUMN_DATA_TYPES = {
    name: ColumnDataTypes.STRING,
    source: ColumnDataTypes.STRING,
    file: ColumnDataTypes.STRING,
    projects: ColumnDataTypes.ENUM,
    users: ColumnDataTypes.ENUM,
    // KIR-5099: AgentMesh levels (mcpRiskLevelSchema).
    risk: ColumnDataTypes.ENUM,
};
/** Fields the Skills filter-options endpoint can enumerate. */
export const SKILL_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    FILE: 'file',
    PROJECTS: 'projects',
    USERS: 'users',
};
export const skillFilterOptionFieldSchema = z.enum([
    SKILL_FILTER_OPTION_FIELDS.NAME,
    SKILL_FILTER_OPTION_FIELDS.FILE,
    SKILL_FILTER_OPTION_FIELDS.PROJECTS,
    SKILL_FILTER_OPTION_FIELDS.USERS,
]);
export const skillFilterOptionsQuerySchema = z.object({
    field: skillFilterOptionFieldSchema,
    /**
     * Bounded because it reaches Postgres as an `ILIKE` pattern: an unbounded
     * string lets a caller push an arbitrarily large pattern through every
     * candidate row. 200 is far past any real project or skill name.
     */
    search: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    /**
     * The table's pill. Without it a dropdown offers values that exist only in
     * the other state, and picking one empties the current view.
     */
    listState: skillListStateFilterSchema.optional(),
});
/**
 * An option with its display text.
 *
 * `value` is what the filter sends back and is deliberately NOT the label:
 * projects key on their slug and users on their id, because two projects can
 * share a display name and a user may have no name at all.
 */
export const skillFilterOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
export const skillFilterOptionsResponseSchema = z.object({
    field: skillFilterOptionFieldSchema,
    options: z.array(z.string()),
    optionsWithMetadata: z.array(skillFilterOptionSchema).optional(),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
export const skillDataTableSortFieldSchema = z.enum([
    'name',
    'source',
    'file',
    'projects',
    'users',
    'listState',
    // KIR-5099: AgentMesh console order.
    'risk',
]);
const skillDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: skillDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    /** Pill filter; omitted or `all` applies no state narrowing. */
    listState: skillListStateFilterSchema.optional(),
    knownTotal: knownTotalQuerySchema,
    /** KIR-4701: resolve each skill's state for this security group. */
    securityGroupId: z.string().uuid().optional(),
});
export const skillDataTableQuerySchema = createTableQuerySchema(skillDataTableQueryBaseSchema, SKILL_COLUMN_DATA_TYPES);
/**
 * What a caller hands {@link listSkillsDataTable}, as opposed to
 * {@link skillDataTableQuerySchema}, which coerces the same fields off a
 * querystring. Everything is optional: the server supplies every default.
 */
export const listSkillsDataTableParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: skillDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    listState: skillListStateFilterSchema.optional(),
    filters: z.array(FiltersSchema.element).optional(),
    knownTotal: knownTotalParamsSchema,
    /** KIR-4701: resolve each skill's state for this security group. */
    securityGroupId: z.string().uuid().optional(),
});
export const skillTableResponseSchema = z.object({
    data: z.array(skillDataTableItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
/**
 * Allow one listed version of a skill (KIR-5174): the Skills row's Allow.
 *
 * The identity exactly as the row carries it. A rule naming this version is
 * removed. A rule naming no version (written before KIR-5174, or through the
 * Management API) marks every version of the skill, so it is replaced, in the
 * same transaction, by one rule per OTHER version the organization has: the
 * row the admin allowed is the only one that changes.
 */
export const skillAllowVersionBodySchema = z
    .object({
    name: z.string().min(1),
    source: z.string().min(1),
    project: z.string().min(1).nullable(),
    version: skillVersionSchema.nullable(),
})
    .strict();
export const skillAllowVersionResponseSchema = z.object({
    /** Rules deleted: the version's own, plus any every-version rule split. */
    removed: z.number().int().nonnegative(),
    /** Per-version rules written to keep the skill's other versions marked. */
    created: z.number().int().nonnegative(),
});
//# sourceMappingURL=skills.js.map